import styled from '@xstyled/styled-components';
import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { shallow } from 'zustand/shallow';
import { select } from '~/lib/select';
import { PretestAnswersProvider } from '~/training/sequence/PretestAnswers';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { translate } from '~/training/sequence/templates';
import { Root } from '~/training/sequence/templates/items/root/Root';
import { useQuizStore } from '~/training/sequence/templates/items/quiz/QuizStore';
import { PlayButtonEds, ReplayButtonEds } from '~/ui/buttons';
import { useGlobalMediaStore } from '~/ui/video-player/GlobalMediaProvider';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { TrainingState, useTrainingStore } from '../state';
import { PretestQuestionView } from './PretestQuestionView';
import {
  canAskWithTemplate,
  PretestQuestion,
  questionOptions,
  slideForQuestion
} from './pretestSlide';

const selector = (s: TrainingState) =>
  select(s, 'companyName', 'logoUrl', 'featureShowLlama');
const narrationSelector = (s: MediaPlayerStore) =>
  select(s, 'status', 'toggle', 'progress', 'currentTime', 'restart', 'play');

// Bottom right, as on a course slide, but above the assessment's own controls.
const NarrationControl = styled.div`
  position: sticky;
  bottom: 8px;
  justify-self: end;
  margin-right: 8px;
  z-index: hud;
`;

/**
 * The question's narration button, as a course slide shows it (ComboButton):
 * the learner plays the narration when it does not play by itself, and can
 * pause it, then replay it once it ends (ReplayMediaBtn). Its own component, so
 * playback progress re-renders only it. A replay still waiting to play belongs
 * to the question, which owns this control, and gives way to the learner's own
 * Play or Pause.
 */
const PretestNarration = () => {
  const { status, toggle, progress, currentTime, restart, play } =
    useMediaPlayerStore(narrationSelector, shallow);
  const pendingReplay = useRef<ReturnType<typeof setTimeout>>();
  const cancelReplay = () => clearTimeout(pendingReplay.current);

  useEffect(() => () => clearTimeout(pendingReplay.current), []);

  if (status === 'done' && currentTime) {
    return (
      <NarrationControl>
        <ReplayButtonEds
          onClick={() => {
            cancelReplay();
            restart();
            pendingReplay.current = setTimeout(() => play(), 300);
          }}
        />
      </NarrationControl>
    );
  }

  if (status !== 'ready' && status !== 'playing' && status !== 'paused') {
    return null;
  }

  return (
    <NarrationControl>
      <PlayButtonEds
        state={status}
        onClick={() => {
          cancelReplay();
          toggle();
        }}
        progress={progress}
      />
    </NarrationControl>
  );
};

/**
 * Asks a drawn pretest question. The slide renders with its regular template
 * when the template asks the question only through answer buttons, which have
 * a pretest mode; any other slide is asked with the plain question view.
 * Either way the slide sits outside SequenceAnswersProvider, so nothing on it
 * can save an answer.
 */
export const PretestQuestionSlide = ({
  question
}: {
  question: PretestQuestion;
}) => {
  const { companyName, logoUrl, featureShowLlama } = useTrainingStore(selector);
  const clearBlocker = useSequenceStore(s => s.clearBlocker);
  const reset = useMediaPlayerStore(s => s.reset);
  const setPlayer = useGlobalMediaStore(s => s.setPlayer);
  const { optionIds, multiple } = useMemo(
    () => questionOptions(question),
    [question]
  );

  const Template = useMemo(() => {
    try {
      const definition = translate({
        ...slideForQuestion(question),
        companyName,
        logoUrl,
        featureShowLlama
      });

      return canAskWithTemplate(definition, optionIds)
        ? new Root(definition).component()
        : null;
    } catch {
      // A slide its template cannot draw is still asked, in the plain view.
      return null;
    }
  }, [question]);

  useLayoutEffect(() => {
    // Start each question clean, as a slide does: no blockers, quiz feedback
    // or media carried over from the previous one.
    clearBlocker();
    useQuizStore.setState({ quizFeedback: {} });
    setPlayer(undefined);
    reset();

    // The pretest page keeps its player for the next question and the
    // results, so a question stops its media when it goes.
    return () => {
      setPlayer(undefined);
      reset();
    };
  }, [question.id]);

  return (
    <>
      <PretestAnswersProvider
        questionId={question.id}
        optionIds={optionIds}
        multiple={multiple}
      >
        {Template ? <Template /> : <PretestQuestionView question={question} />}
      </PretestAnswersProvider>
      <PretestNarration key={question.id} />
    </>
  );
};
